import { useCallback, useEffect, useRef, useState } from 'react';

// Client half of the spam shield (server half: api/lib/spam-shield.ts).
//
// Supplies the three fields the server verifies:
//   • turnstile_token  — from the Turnstile widget
//   • company_website  — the honeypot; a hidden field only a bot fills
//   • elapsed_ms       — how long the form was on screen before submit
//
// The honeypot name is deliberately plausible: bots fill fields whose names
// look real, and "company_website" is exactly the sort of thing they target on
// a marketing form.
//
// `ready` gates the submit button, so it must never latch off. An ad blocker
// or a Cloudflare outage stops the widget from ever producing a token, and a
// button that is disabled forever with no explanation loses the lead more
// quietly than any server-side rejection would. So Turnstile resolves to one
// of three states, and both terminal states unblock the button: with a token
// the server verifies it, without one the server explains what went wrong.

export const HONEYPOT_FIELD = 'company_website';

/**
 * How long to wait for a token before treating the widget as unavailable.
 * Covers every silent failure mode at once — blocked script, blocked iframe,
 * a widget that renders but never calls back.
 */
const TURNSTILE_TIMEOUT_MS = 12_000;

export type TurnstileState =
  /** Still waiting for the widget to solve. */
  | 'pending'
  /** Widget produced a token. */
  | 'solved'
  /** Blocked, errored or timed out — no token is coming. */
  | 'unavailable';

export interface FormShieldPayload {
  turnstile_token: string | null;
  company_website: string;
  elapsed_ms: number;
}

export interface FormShield {
  /** Merge into the request body sent to /api/leads/submit. */
  payload: () => FormShieldPayload;
  /** Wire to the hidden honeypot input. */
  honeypot: {
    name: string;
    value: string;
    onChange: (_e: { target: { value: string } }) => void;
  };
  /** Pass to <TurnstileWidget onVerify=...>. */
  onTurnstileVerify: (_token: string | null) => void;
  /** Pass to <TurnstileWidget onUnavailable=...>. */
  onTurnstileUnavailable: () => void;
  /** Pass to <TurnstileWidget resetSignal=...>. */
  resetSignal: number;
  /**
   * Discard the current token and ask for a new one. Call after a submission
   * the server did not accept: the spent token would fail every retry.
   */
  resetToken: () => void;
  /** Current Turnstile state; 'unavailable' when it is not configured at all. */
  turnstileState: TurnstileState;
  /**
   * Whether the form may be submitted. False only while Turnstile is still
   * working — never permanently.
   */
  ready: boolean;
}

export function useFormShield(): FormShield {
  const mountedAt = useRef<number>(Date.now());
  const [honeypotValue, setHoneypotValue] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [resetSignal, setResetSignal] = useState(0);

  const configured = !!import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const [state, setState] = useState<TurnstileState>(
    // With no site key there is no widget to wait for; the honeypot and timing
    // checks still apply server-side.
    configured ? 'pending' : 'unavailable'
  );

  // Backstop for a widget that fails without telling us — the most common
  // shape of this problem, since a blocked request often produces no error
  // event at all.
  useEffect(() => {
    if (!configured) return;
    const timer = setTimeout(() => {
      setState(current => (current === 'pending' ? 'unavailable' : current));
    }, TURNSTILE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [configured]);

  const payload = useCallback(
    (): FormShieldPayload => ({
      turnstile_token: token,
      company_website: honeypotValue,
      elapsed_ms: Date.now() - mountedAt.current,
    }),
    [token, honeypotValue]
  );

  const onTurnstileVerify = useCallback((next: string | null) => {
    setToken(next);
    // A null token means the previous one expired: go back to pending and let
    // the widget re-solve, or let the timeout call it unavailable.
    setState(next ? 'solved' : 'pending');
  }, []);

  const onTurnstileUnavailable = useCallback(() => {
    setToken(null);
    setState('unavailable');
  }, []);

  const resetToken = useCallback(() => {
    setToken(null);
    setResetSignal(n => n + 1);
    // Only wait on the widget again if there is one; otherwise 'unavailable'
    // must stick, or the submit button latches off.
    setState(current => (configured ? 'pending' : current));
  }, [configured]);

  return {
    payload,
    honeypot: {
      name: HONEYPOT_FIELD,
      value: honeypotValue,
      onChange: e => setHoneypotValue(e.target.value),
    },
    onTurnstileVerify,
    onTurnstileUnavailable,
    resetSignal,
    resetToken,
    turnstileState: state,
    ready: state !== 'pending',
  };
}
